import React, {useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, useForm, useWatch} from 'react-hook-form';

import {useTranslation} from 'react-i18next';

import {
    ifCategoryOfO,
    isStandartCategory,
    VAT_CATEGORY,
} from 'components/pages/DocumentsCreate/InvoiceNew/helpers/consts';

import {CustomRadioGroup} from 'components/pages/DocumentsCreate/InvoiceNew/SimpleRadio';
import {Button, ButtonVariant} from 'components/ui-kit/Button';

import {Input} from 'components/ui-kit/Input';
import {Select} from 'components/ui-kit/Select';
import {Switch} from 'components/ui-kit/Switcher';

import {CURRENCY} from 'core/constants/currency';
import {$allowedActionsForInvoice} from 'models/documents/invoices/common';
import {updateTaxesAndTotalAllowanceCharge} from 'models/documents/invoices/create';

import {AllowanceCharge} from 'types/invoices';

import {chargesTableSchema} from '../../validation';
import {TaxesTable} from '../InnerTable';

import {FlexWrapper, Grid, HeadControlElement, Headline, Label} from './styled';

type Props = {print?: boolean};
export const ChargesList = ({print}: Props) => {
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);
    const {
        control,
        setValue,
        getValues,
        reset,
        trigger,
        formState: {errors},
    } = useForm({
        defaultValues: {
            isCharge: 'charge',
            isRatePercent: false,
            reason: '',
            rate: '',
            baseAmount: '',
            totalAmount: '',
            vatCategory: null,
            vat: '0',
            remove: '',
        },
        resolver: yupResolver(chargesTableSchema),
    });
    const [vatCategory, setVatCategory] = useState<string>();
    const [withRate, setWithRate] = useState(false);
    const {t: translation} = useTranslation();
    const GEN_INFO = 'pages.documents.invoices.create.taxesTotal.upd';
    const t = (key: string) => translation(`${GEN_INFO}.${key}`);

    const chargeAllowance = useWatch<'charge' | 'allowance'>({
        name: 'isCharge',
        control,
        defaultValue: 'charge',
    });

    const heads = useMemo(
        () => [
            {
                label: t('reason'),
                name: 'reason',
                required: true,
                disabled: false,
                type: 'text',
            },
            {
                label: t('rate'),
                name: 'rate',
                required: withRate,
                disabled: !withRate,
                type: 'number',
            },
            {
                label: t('baseAmount'),
                name: 'baseAmount',
                required: withRate,
                disabled: !withRate,
                type: 'number',
            },
            {
                label: `${t('totalAmount')} (${CURRENCY})`,
                name: 'totalAmount',
                required: !withRate,
                disabled: withRate,
                type: 'number',
            },
            {
                label: t('vatCategory'),
                name: 'vatCategory',
                required: true,
                disabled: false,
                type: 'select',
            },
            {
                label: t('vat'),
                name: 'vat',
                required: false,
                disabled: !isStandartCategory(vatCategory),
                type: 'number',
            },
            {
                label: '',
                name: 'remove',
                required: false,
                disabled: false,
                type: 'button',
            },
        ],
        [vatCategory, withRate],
    );

    function clearRateRelatedValues() {
        setValue('rate', null);
        setValue('baseAmount', null);
        setValue('totalAmount', null);
    }

    function handleAddItem() {
        const values = getValues();
        const {reason, isRatePercent, rate, baseAmount, totalAmount, vatCategory, vat} = values;

        const payload: AllowanceCharge = {
            reason,
            rate: withRate ? rate : '',
            baseAmount: withRate ? baseAmount : '',
            totalAmount,
            vatCategory,
            vat,
            isCharge: chargeAllowance,
            isRatePercent,
        };

        updateTaxesAndTotalAllowanceCharge(payload);
        reset();
    }

    function calc() {
        const {rate, baseAmount} = getValues(['rate', 'baseAmount']);
        const r = Number(rate) || 0;
        const b = Number(baseAmount) || 0;
        setValue('totalAmount', (b / 100) * r);
    }

    function handleVatValue(category: AllowanceCharge['vatCategory']) {
        if (category === null) {
            return;
        }
        const {value} = category;
        setVatCategory(value);
    }

    useEffect(() => {
        if (!vatCategory) {
            return;
        }
        if (isStandartCategory(vatCategory)) {
            return;
        }
        if (ifCategoryOfO(vatCategory)) {
            setValue('vat', null);
            return;
        }
        setValue('vat', '0');
    }, [vatCategory]);

    return (
        <div>
            {allowedActionsForInvoice.edit && (
                <FlexWrapper style={{width: '40vw'}}>
                    <Label>{t('chargesAllowance')}</Label>
                    <Controller
                        control={control}
                        name={'isCharge'}
                        defaultValue={chargeAllowance}
                        render={(props) => {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            const {ref, value, ...rest} = props;
                            return (
                                <CustomRadioGroup
                                    {...rest}
                                    value={value}
                                    options={[
                                        {value: 'charge', label: t('charges')},
                                        {value: 'allowance', label: t('allowance')},
                                    ]}
                                />
                            );
                        }}
                    />
                </FlexWrapper>
            )}
            {allowedActionsForInvoice.edit && (
                <FlexWrapper style={{width: '40vw'}}>
                    <Label>{t('addDiscount')}</Label>
                    <Controller
                        control={control}
                        name={'isRatePercent'}
                        // defaultValue={withRate}
                        render={(props) => {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            const {ref, ...rest} = props;
                            return (
                                <Switch
                                    {...rest}
                                    onChange={(e: boolean) => {
                                        props.onChange(e);
                                        clearRateRelatedValues();
                                        setWithRate(e);
                                        trigger();
                                    }}
                                />
                            );
                        }}
                    />
                </FlexWrapper>
            )}
            <Grid>
                {heads.map((i, index) => (
                    <Headline key={`${index}_${i.label}`} required={!print && i.required}>
                        {i.label}
                    </Headline>
                ))}
                {allowedActionsForInvoice.edit &&
                    heads.map((i, index) => (
                        <HeadControlElement key={`${index}_${i.name}`}>
                            <Controller
                                key={index}
                                control={control}
                                name={i.name}
                                // defaultValue={''}
                                render={(props) => {
                                    if (i.type === 'button') {
                                        return <></>;
                                    }
                                    if (i.type === 'select') {
                                        return (
                                            <Select
                                                {...props}
                                                options={VAT_CATEGORY}
                                                onChange={(q: AllowanceCharge['vatCategory']) => {
                                                    props.onChange(q);
                                                    handleVatValue(q);
                                                    trigger();
                                                }}
                                                menuPosition={'fixed'}
                                                hasError={i.name in errors}
                                            />
                                        );
                                    }
                                    return (
                                        <Input
                                            {...props}
                                            type={i.type}
                                            min={0}
                                            onChange={(v) => {
                                                props.onChange(v);
                                                if (i.name === 'rate' || i.name === 'baseAmount') {
                                                    calc();
                                                }
                                                trigger();
                                            }}
                                            disabled={i.disabled}
                                            hasError={i.name in errors}
                                        />
                                    );
                                }}
                            />
                        </HeadControlElement>
                    ))}
                <div />
                {allowedActionsForInvoice.edit && (
                    <div style={{gridColumn: '1/-1'}}>
                        <Button
                            style={{width: 'auto'}}
                            size="md"
                            type="button"
                            onClick={async () => {
                                const isValid = await trigger();
                                if (!isValid) {
                                    console.warn('Form contains errors:', errors);
                                    return;
                                }
                                handleAddItem();
                            }}
                            variant={ButtonVariant.Secondary}
                        >
                            {t('addChargesAllowance')}
                        </Button>
                    </div>
                )}
                <TaxesTable />
            </Grid>
        </div>
    );
};
